import React, { useState, useEffect } from 'react';
import { Container, Button, Modal, Alert, Table, Form, Badge, Spinner, Row, Col } from "react-bootstrap";
import { LanguageProvider } from '../../../core/language-provider';
import { ApiManager } from '../../../core/api-manager';
import { CacheManager } from '../../../core/cache-manager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { settings } from '../../../config/settings';
import './ImportTable.css';

export default function ModalEntityImport({ entity, props, status, close, refreshData }) {

    const [step, setStep] = useState(1); // 1: upload, 2: preview, 3: result
    const [loading, setLoading] = useState(false);
    const [fileData, setFileData] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [error, setError] = useState('');
    const [showOnlyErrors, setShowOnlyErrors] = useState(false);

    // Check if entity is a SQL view (starts with gyv_)
    const isSqlView = entity && entity.startsWith('gyv_');

    useEffect(() => {
        if (!status) {
            // Reset when modal closes
            setStep(1);
            setFileData(null);
            setParsedData(null);
            setImportResult(null);
            setError('');
        }
    }, [status]);

    function closeModal() {
        close();
    }

    // Download template
    async function downloadTemplate() {
        setLoading(true);
        setError('');
        
        try {
            const response = await ApiManager.sendAuthenticatedRequest('/utils/import_download_template', {
                entity: entity
            });
            
            if (response.success === 1) {
                // Create download link
                const binaryString = atob(response.data.content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: response.data.mimetype });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = response.data.filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                setError(response.message || LanguageProvider.get('import.download_template_error') || 'Failed to download template');
            }
        } catch (e) {
            setError(LanguageProvider.get('import.download_template_error') || 'Error downloading template');
        }
        
        setLoading(false);
    }

    // Download full data
    async function downloadFullData() {
        setLoading(true);
        setError('');
        
        // Always use export for import (with labels)
        await exportForImport(1, 100000);
    }
    async function exportForImport(page, limit) {
        try {
            const response = await ApiManager.sendAuthenticatedRequest('/utils/export_for_import', {
                entity: entity,
                offset: (page - 1) * limit,
                limit: limit,
                order: ' id ASC',
                query: null,
                fields: null,
                constraints: null
            });
            
            if (response.success === 1 && response.data) {
                // Download the file (Excel or CSV)
                const binaryString = atob(response.data.content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: response.data.mimetype });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = response.data.filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                
            } else {
                setError(response.message || LanguageProvider.get('import.export_failed'));
            }
        } catch (e) {
            setError(LanguageProvider.get('import.export_error'));
        }
        
        setLoading(false)
    }


    // Handle file upload
    function handleFileUpload(e) {
        const file = e.target.files[0];
        if (!file) return;
        
        // Check file type
        const validTypes = ['.csv', '.xlsx', '.xls'];
        const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        
        if (!validTypes.includes(fileExt)) {
            setError(LanguageProvider.get('import.invalid_file_type'));
            return;
        }
        
        setError('');
        const reader = new FileReader();
        
        reader.onload = (e) => {
            const content = e.target.result;
            // Convert ArrayBuffer to base64 in chunks to avoid stack overflow
            const base64 = arrayBufferToBase64(content);
            setFileData({
                name: file.name,
                content: base64,
                type: fileExt
            });
            parseFile(base64);
        };
        
        reader.readAsArrayBuffer(file);
    }
    
    // Helper function to convert ArrayBuffer to base64 in chunks
    function arrayBufferToBase64(buffer) {
        const bytes = new Uint8Array(buffer);
        const chunkSize = 8192; // Process 8KB at a time
        let binary = '';
        
        for (let i = 0; i < bytes.length; i += chunkSize) {
            const chunk = bytes.slice(i, i + chunkSize);
            binary += String.fromCharCode.apply(null, chunk);
        }
        
        return btoa(binary);
    }

    // Parse uploaded file
    async function parseFile(fileContent) {
        setLoading(true);
        setError('');
        
        try {
            const response = await ApiManager.sendAuthenticatedRequest('/utils/import_parse_file', {
                entity: entity,
                fileContent: fileContent
            });
            
            if (response.success === 1) {
                setParsedData(response.data);
                setStep(2);
            } else {
                setError(response.message || LanguageProvider.get('import.parse_file_error'));
                if (response.errors) {
                    setError(error + '\n' + response.errors.join('\n'));
                }
            }
        } catch (e) {
            setError(LanguageProvider.get('import.parse_file_error'));
        }
        
        setLoading(false);
    }

    // Execute import
    async function executeImport() {
        if (!parsedData || !parsedData.rows) return;
        
        setLoading(true);
        setError('');
        
        try {
            const response = await ApiManager.sendAuthenticatedRequest('/utils/import_execute', {
                entity: entity,
                rows: parsedData.rows
            });
            
            if (response.success === 1) {
                setImportResult(response.results);
                setStep(3);
                
                // Clear cache for the imported entity
                await CacheManager.invalidateEntity(entity);
                
                // Refresh the data grid
                if (refreshData) {
                    console.log('Refreshing data after import')
                    refreshData();
                }
            } else {
                setError(response.message || LanguageProvider.get('import.import_error'));
                if (response.results && response.results.errors) {
                    const errorMessages = response.results.errors.map(e => 
                        `Row ${e.row}: ${e.errors.join(', ')}`
                    ).join('\n');
                    setError(error + '\n' + errorMessages);
                }
            }
        } catch (e) {
            setError(LanguageProvider.get('import.import_execute_error'));
        }
        
        setLoading(false);
    }

    // Reset to start
    function reset() {
        setStep(1);
        setFileData(null);
        setParsedData(null);
        setImportResult(null);
        setError('');
    }

    // Get field type icon
    function getFieldIcon(fieldName, fieldConfig) {
        if (!fieldConfig) return <FontAwesomeIcon icon="question" className="field-icon me-1" />;
        
        const type = fieldConfig.input_type || 'string';
        const iconMap = {
            string: 'font',
            text: 'align-left',
            richText: 'edit',
            number: 'hashtag',
            float: 'calculator',
            currency: 'dollar-sign',
            boolean: 'toggle-on',
            date: 'calendar-day',
            datetime: 'clock',
            time: 'clock',
            option: 'list',
            externalEntity: 'link',
            file: 'file',
            password: 'lock',
            color: 'palette',
            address: 'map-marker-alt'
        };
        
        return <FontAwesomeIcon icon={iconMap[type] || 'question'} className="field-icon me-1" />;
    }

    // Get row status badge
    function getRowStatusBadge(row) {
        if (row.errors && row.errors.length > 0) {
            return <Badge bg="danger">{LanguageProvider.get('import.error_badge')}</Badge>;
        }
        if (row.warnings && row.warnings.length > 0) {
            return <Badge bg="warning">{LanguageProvider.get('import.warning_badge')}</Badge>;
        }
        if (row.action === 'update') {
            return <Badge bg="info">{LanguageProvider.get('import.update_badge')}</Badge>;
        }
        return <Badge bg="success">{LanguageProvider.get('import.insert_badge')}</Badge>;
    }

    // Get field display value
    function getFieldDisplay(row, fieldName) {
        const value = row.data[fieldName];
        
        // Check if this is a resolved external entity
        if (row._resolved && row._resolved[fieldName]) {
            const resolved = row._resolved[fieldName];
            return (
                <span>
                    {resolved.label}
                    <Badge bg="secondary" className="ms-1">ID: {resolved.id}</Badge>
                </span>
            );
        }
        
        // Regular value
        if (value === null || value === undefined) {
            return <span className="text-muted">-</span>;
        }
        
        if (typeof value === 'boolean') {
            return value ? LanguageProvider.get('import.yes') : LanguageProvider.get('import.no');
        }
        
        return String(value);
    }

    if (props !== null && status !== false) {
        return (
            <>
                <Modal 
                    show={status} 
                    onHide={closeModal} 
                    size={settings.modalDefaultSize && settings.modalDefaultSize.entityImport ? settings.modalDefaultSize.entityImport : "xl"} 
                    scrollable={true}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {LanguageProvider.get('defaults.import')} - {LanguageProvider.get('entity.'+entity)}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body id="modal-entity-import-body">
                        {isSqlView && (
                            <Container className="text-center p-5">
                                <FontAwesomeIcon icon="exclamation-triangle" size="3x" className="text-warning mb-3" />
                                <h5 className="text-warning">{LanguageProvider.get('import.import_not_available')}</h5>
                                <p className="text-muted">
                                    {LanguageProvider.get('import.sql_view_notice')}<br />
                                    {LanguageProvider.get('import.sql_view_desc').replace('{entity}', entity)}
                                </p>
                                <Alert variant="info" className="mt-3">
                                    <strong>Note:</strong> {LanguageProvider.get('import.sql_view_note')}
                                </Alert>
                            </Container>
                        )}
                        
                        {!isSqlView && loading && (
                            <div className="text-center p-5">
                                <Spinner animation="border" />
                                <p className="mt-3">{LanguageProvider.get('import.processing')}</p>
                            </div>
                        )}
                        
                        {!isSqlView && !loading && step === 1 && (
                            <>
                                <Container className="mb-4">
                                    <h5>{LanguageProvider.get('import.step1_title')}</h5>
                                    <p className="text-muted">
                                        {LanguageProvider.get('import.step1_desc')}
                                    </p>
                                    
                                    <Row className="mb-4">
                                        <Col md={6}>
                                            <Button 
                                                variant="outline-primary" 
                                                onClick={downloadTemplate}
                                                className="w-100"
                                            >
                                                <FontAwesomeIcon icon="download" className="me-2" />
                                                {LanguageProvider.get('import.download_template')}
                                            </Button>
                                        </Col>
                                        <Col md={6}>
                                            <Button 
                                                variant="outline-success" 
                                                onClick={downloadFullData}
                                                className="w-100"
                                            >
                                                <FontAwesomeIcon icon="download" className="me-2" />
                                                {LanguageProvider.get('import.download_full_dataset')}
                                            </Button>
                                        </Col>
                                    </Row>
                                    
                                    <Form.Group className="mb-3">
                                        <Form.Label>{LanguageProvider.get('import.upload_file')}</Form.Label>
                                        <Form.Control 
                                            type="file" 
                                            accept=".csv,.xlsx,.xls"
                                            onChange={handleFileUpload}
                                        />
                                        <Form.Text className="text-muted">
                                            {LanguageProvider.get('import.accepted_formats')}
                                        </Form.Text>
                                    </Form.Group>
                                    
                                    {fileData && (
                                        <Alert variant="info">
                                            {LanguageProvider.get('import.file_loaded')}: {fileData.name}
                                        </Alert>
                                    )}
                                </Container>
                                
                                <Container className="border rounded p-3 bg-light">
                                    <h6>{LanguageProvider.get('import.instructions')}</h6>
                                    <ul className="small">
                                        <li>{LanguageProvider.get('import.instruction1')}</li>
                                        <li>{LanguageProvider.get('import.instruction2')}</li>
                                        <li>{LanguageProvider.get('import.instruction3')}</li>
                                        <li>{LanguageProvider.get('import.instruction4')}</li>
                                        <li>{LanguageProvider.get('import.instruction5')}</li>
                                    </ul>
                                </Container>
                            </>
                        )}
                        
                        {!isSqlView && !loading && step === 2 && parsedData && (
                            <>
                                <Container className="mb-4">
                                    <h5>{LanguageProvider.get('import.step2_title')}</h5>
                                    <p className="text-muted">
                                        {LanguageProvider.get('import.step2_desc')}
                                    </p>
                                    
                                    <Row className="mb-3">
                                        <Col>
                                            <Badge bg="success">{LanguageProvider.get('import.valid_rows')}: {parsedData.rows.filter(r => !r.errors || r.errors.length === 0).length}</Badge>
                                            {' '}
                                            <Badge bg="danger">{LanguageProvider.get('import.errors')}: {parsedData.rows.filter(r => r.errors && r.errors.length > 0).length}</Badge>
                                            {' '}
                                            <Badge bg="info">{LanguageProvider.get('import.updates')}: {parsedData.rows.filter(r => r.action === 'update').length}</Badge>
                                            {' '}
                                            <Badge bg="primary">{LanguageProvider.get('import.inserts')}: {parsedData.rows.filter(r => r.action === 'insert').length}</Badge>
                                        </Col>
                                    </Row>
                                    
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Check
                                                type="switch"
                                                label={LanguageProvider.get('import.show_only_errors')}
                                                checked={showOnlyErrors}
                                                onChange={(e) => setShowOnlyErrors(e.target.checked)}
                                            />
                                        </Col>
                                    </Row>
                                    
                                    <div className="import-table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <table className="import-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 50 }}>{LanguageProvider.get('import.row')}</th>
                                                    <th style={{ width: 100 }}>{LanguageProvider.get('import.status')}</th>
                                                    {parsedData.headers.map((header, i) => (
                                                        <th key={i}>
                                                            {getFieldIcon(header, parsedData.importableFields?.[header])}
                                                            {LanguageProvider.getLabel(entity, header) || header}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {parsedData.rows
                                                    .filter(row => !showOnlyErrors || (row.errors && row.errors.length > 0))
                                                    .map((row, i) => (
                                                    <tr key={i} className={row.errors && row.errors.length > 0 ? 'table-danger' : ''}>
                                                        <td>{row._original_row || i + 1}</td>
                                                        <td>{getRowStatusBadge(row)}</td>
                                                        {parsedData.headers.map((header, j) => (
                                                            <td key={j}>
                                                                {getFieldDisplay(row, header)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    {/* Show errors */}
                                    {parsedData.rows.some(r => r.errors && r.errors.length > 0) && (
                                        <Alert variant="danger" className="mt-3">
                                            <h6>{LanguageProvider.get('import.errors_found')}</h6>
                                            <ul className="small mb-0">
                                                {parsedData.rows.filter(r => r.errors && r.errors.length > 0).map((row, i) => (
                                                    <li key={i}>
                                                        Row {row._original_row || i + 1}: {row.errors.join(', ')}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}
                                </Container>
                            </>
                        )}
                        
                        {!isSqlView && !loading && step === 3 && importResult && (
                            <>
                                <Container className="mb-4">
                                    <h5>{LanguageProvider.get('import.step3_title')}</h5>
                                    
                                    <Alert variant="success">
                                        <h6>{LanguageProvider.get('import.import_summary')}</h6>
                                        <ul className="mb-0">
                                            <li>{LanguageProvider.get('import.records_inserted')}: {importResult.inserted}</li>
                                            <li>{LanguageProvider.get('import.records_updated')}: {importResult.updated}</li>
                                            {importResult.errors && importResult.errors.length > 0 && (
                                                <li>{LanguageProvider.get('import.errors')}: {importResult.errors.length}</li>
                                            )}
                                        </ul>
                                    </Alert>
                                    
                                    {importResult.errors && importResult.errors.length > 0 && (
                                        <Alert variant="danger">
                                            <h6>{LanguageProvider.get('import.errors')}:</h6>
                                            <ul className="small mb-0">
                                                {importResult.errors.map((err, i) => (
                                                    <li key={i}>
                                                        {LanguageProvider.get('import.row')} {err.row}: {err.errors.join(', ')}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}
                                </Container>
                            </>
                        )}
                        
                        {error && (
                            <Alert variant="danger" className='mt-3'>
                                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{error}</pre>
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        {isSqlView && (
                            <Button variant="secondary" onClick={closeModal}>
                                {LanguageProvider.get('defaults.close')}
                            </Button>
                        )}
                        
                        {!isSqlView && step === 1 && (
                            <>
                                <Button variant="secondary" onClick={closeModal}>
                                    {LanguageProvider.get('defaults.close')}
                                </Button>
                            </>
                        )}
                        
                        {!isSqlView && step === 2 && (
                            <>
                                <Button variant="warning" onClick={reset}>
                                    <FontAwesomeIcon icon="arrow-left" className="me-2" />
                                    {LanguageProvider.get('import.back')}
                                </Button>
                                <Button 
                                    variant="success" 
                                    onClick={executeImport}
                                    disabled={!parsedData || parsedData.rows.some(r => r.errors && r.errors.length > 0)}
                                >
                                    <FontAwesomeIcon icon="upload" className="me-2" />
                                    {LanguageProvider.get('import.execute_import')}
                                </Button>
                                <Button variant="secondary" onClick={closeModal}>
                                    {LanguageProvider.get('defaults.cancel')}
                                </Button>
                            </>
                        )}
                        
                        {!isSqlView && step === 3 && (
                            <>
                                <Button variant="primary" onClick={reset}>
                                    <FontAwesomeIcon icon="plus" className="me-2" />
                                    {LanguageProvider.get('import.import_more')}
                                </Button>
                                <Button variant="secondary" onClick={closeModal}>
                                    {LanguageProvider.get('defaults.close')}
                                </Button>
                            </>
                        )}
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
    
    return null;
};